import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { collection, query, onSnapshot, where, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { Bell, ChevronRight } from 'lucide-react';
import { isNotificationClickable, navigateFromNotification } from '../../utils/userNotifications';
import { filterBellsForViewer } from '../../utils/portalPartnerBells';
import {
  installBellSoundUnlock,
  isBellSoundEnabled,
  playBellNotificationSound,
  shouldPlayBellSound,
} from '../../utils/bellNotificationSound';
import NotificationRow from './NotificationRow';
import { saveProjectNotificationPref } from '../../utils/projectNotificationPrefs';
import CommentReactionPulse from './CommentReactionPulse';
import '../pm/pm-shared.css';
import './NotificationCenter.css';

const MENU_CLOSE_DELAY_MS = 200;
type HeaderMenu = 'none' | 'notifications' | 'reactions';

interface Notification {
  id: string;
  type: 'new_lead' | 'lead_qualified' | 'deal_won' | 'system' | 'new_message' | 'mention' | 'task_mention' | 'board_mention' | 'board_comment' | 'portal_feedback' | 'portal_upload' | 'portal_deliverable_approval' | 'proposal_comment' | 'proposal_ops_review_requested' | 'proposal_ops_review_approved' | 'task_comment' | 'architect_comment' | 'external_comment' | 'pdf_comment' | 'pdf_external_comment' | 'task_assigned' | 'lead_todo_assigned' | 'project_file_version' | 'plan_review_complete' | 'supplier_rfq_upload' | 'card_expense_digest' | 'contractor_invoice' | 'billing_payables' | 'capital_plan' | 'designer_invoice_review' | 'qbo_bill_office' | 'task_reminder' | 'bug_squashed' | 'job_application' | 'crew_job_commit' | 'contractor_onboard';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  leadId?: string;
  dealId?: string;
  sourceId?: string;
  phoneNumber?: string;
  isPriority?: boolean;
  projectType?: string;
  portalStep?: string;
  projectId?: string;
  taskId?: string;
  leadTodoId?: string;
  firestoreId?: string;
  streamId?: string;
  opsTaskId?: string;
  /** PDF file id for Project Hub Files deep link */
  documentId?: string;
  pdfPageNumber?: number;
  /** Consolidated group comment thread key (deep link to `?group=`). */
  groupCommentKey?: string;
  discussion?: 'architect' | 'comments' | 'pdf';
  link?: string;
}

interface NotificationCenterProps {
  onLeadClick?: (leadId: string, tab?: 'details' | 'activities' | 'tracking') => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onLeadClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState<HeaderMenu>('none');
  const [instantSwap, setInstantSwap] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const seenUnreadIdsRef = useRef<Set<string> | null>(null);
  const showNotifications = menu === 'notifications';
  const showReactions = menu === 'reactions';

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeMenu = useCallback(() => {
    cancelClose();
    setInstantSwap(false);
    setMenu('none');
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setInstantSwap(false);
      setMenu('none');
      closeTimerRef.current = null;
    }, MENU_CLOSE_DELAY_MS);
  }, [cancelClose]);

  const openMenu = useCallback((next: Exclude<HeaderMenu, 'none'>) => {
    cancelClose();
    setInstantSwap(menu !== 'none' && menu !== next);
    setMenu(next);
  }, [cancelClose, menu]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => installBellSoundUnlock(), []);

  useEffect(() => {
    seenUnreadIdsRef.current = null;
  }, [user?.id]);

  // Hydrate bell dropdown from Firestore on mount
  useEffect(() => {
    if (!user?.id) return;

    const notificationsRef = collection(db, 'userNotifications');
    // Simple query — no orderBy to avoid composite index requirement
    const q = query(
      notificationsRef,
      where('userId', '==', user.id)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const firestoreNotifs: Notification[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        // Bell only shows unread notifications
        if (data.read) return;
        firestoreNotifs.push({
          id: `fs-${docSnap.id}`,
          type: data.type,
          title: data.title,
          message: data.message,
          timestamp: data.createdAt?.toDate?.() || new Date(),
          read: false,
          leadId: data.leadId || undefined,
          dealId: data.dealId || undefined,
          phoneNumber: data.phoneNumber || undefined,
          isPriority: data.isPriority || false,
          projectType: data.projectType || undefined,
          portalStep: data.portalStep || undefined,
          projectId: data.projectId || undefined,
          taskId: data.taskId || undefined,
          leadTodoId: data.leadTodoId || undefined,
          streamId: data.streamId || undefined,
          sourceId: typeof data.sourceId === 'string' ? data.sourceId : undefined,
          opsTaskId: typeof data.opsTaskId === 'string' ? data.opsTaskId : undefined,
          documentId: typeof data.documentId === 'string' ? data.documentId : undefined,
          pdfPageNumber: typeof data.pdfPageNumber === 'number' ? data.pdfPageNumber : undefined,
          groupCommentKey: typeof data.groupCommentKey === 'string' ? data.groupCommentKey : undefined,
          discussion: data.discussion === 'architect' || data.discussion === 'comments' || data.discussion === 'pdf'
            ? data.discussion
            : undefined,
          link: typeof data.link === 'string' ? data.link : undefined,
          firestoreId: docSnap.id,
        });
      });

      // Sort client-side: newest first
      firestoreNotifs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      // Firestore is the single source of truth — bell only shows unread
      const visible = filterBellsForViewer(firestoreNotifs, user).slice(0, 50);
      const nextIds = visible.map((n) => n.firestoreId).filter((id): id is string => !!id);
      const decision = shouldPlayBellSound(seenUnreadIdsRef.current, nextIds);
      seenUnreadIdsRef.current = decision.next;
      if (decision.play && isBellSoundEnabled({ bellSound: user?.bellSound })) {
        playBellNotificationSound({ id: user?.bellSoundId });
      }
      setNotifications(visible);
    }, (error) => {
      console.error('Error hydrating notifications from Firestore:', error);
    });

    return () => unsubscribe();
  }, [user?.id, user?.role, user?.designations, user?.orgLevel, user?.executiveBriefing, user?.bellSound, user?.bellSoundId]);

  // Mark all unread as read in Firestore
  const markAllAsRead = useCallback(async () => {
    const unreadNotifs = notifications.filter(n => !n.read);
    if (unreadNotifs.length === 0) return;

    const batch = writeBatch(db);
    for (const notif of unreadNotifs) {
      if (notif.firestoreId) {
        batch.update(doc(db, 'userNotifications', notif.firestoreId), { read: true });
      }
    }
    try {
      await batch.commit();
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  }, [notifications]);

  // Close panel when clicking outside — do NOT mark unread as read on dismiss
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeMenu]);

  // Lead and SMS notifications are created server-side by Firestore triggers
  // (onLeadCreated, onSmsReceived) which broadcast to ALL employees.

  // Count unread notifications
  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  const handleLeadClick = (leadId: string, tab?: 'details' | 'activities' | 'tracking') => {
    closeMenu();
    if (onLeadClick) {
      onLeadClick(leadId, tab);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.firestoreId && !notification.read) {
      try {
        const batch = writeBatch(db);
        batch.update(doc(db, 'userNotifications', notification.firestoreId), { read: true });
        await batch.commit();
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }

    await navigateFromNotification(notification, {
      navigate,
      onLeadClick: handleLeadClick,
      onClose: closeMenu,
      viewerRole: user?.role,
    });
  };


  const toggleRead = useCallback(async (notification: Notification) => {
    if (!notification.firestoreId) return;
    try {
      await updateDoc(doc(db, 'userNotifications', notification.firestoreId), { read: !notification.read });
    } catch (err) {
      console.error('Error toggling notification read:', err);
    }
  }, []);

  const markSelectedRead = useCallback(async () => {
    const targets = notifications.filter((n) => selectedIds.has(n.id) && n.firestoreId);
    if (targets.length === 0) return;
    const batch = writeBatch(db);
    for (const n of targets) {
      if (n.firestoreId) batch.update(doc(db, 'userNotifications', n.firestoreId), { read: true });
    }
    try {
      await batch.commit();
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error marking selected notifications read:', err);
    }
  }, [notifications, selectedIds]);

  const setProjectMode = useCallback(async (projectId: string | undefined, mode: 'muted' | 'mentions') => {
    if (!user?.id || !projectId) return;
    try {
      await saveProjectNotificationPref({ db, userId: user.id, projectId, mode });
    } catch (err) {
      console.error('Error saving project notification pref:', err);
      window.alert('Could not update project notification setting.');
    }
  }, [user?.id]);

  const clearNotifications = async () => {
    // Explicit "Mark all read" — unread stay until this or an item click
    await markAllAsRead();
  };

  return (
    <div
      className={`notification-center${instantSwap ? ' is-instant' : ''}`}
      ref={panelRef}
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        scheduleClose();
      }}
    >
      <div className="notification-actions">
        <button
          type="button"
          className={`notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
          aria-label="Notifications"
          aria-expanded={showNotifications}
          aria-haspopup="dialog"
          onMouseEnter={() => openMenu('notifications')}
          onFocus={() => openMenu('notifications')}
          onClick={() => openMenu('notifications')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </button>
        <CommentReactionPulse
          panelOpen={showReactions}
          onOpen={() => openMenu('reactions')}
          onClose={closeMenu}
        />
      </div>

      {menu !== 'none' ? (
        <div className="notification-hover-bridge" aria-hidden />
      ) : null}

      {showNotifications && (
        <div className="notification-panel" role="dialog" aria-label="Notifications">
          <div className="notification-header">
            <h3>Notifications</h3>
            {notifications.length > 0 && (
              <button className="clear-btn" onClick={clearNotifications}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-content">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <Bell size={18} />
                <p>No new notifications</p>
              </div>
            ) : (
              <div className="notification-section">
                {selectedIds.size > 0 && (
                  <div className="notification-bulk-bar">
                    <button type="button" className="clear-btn" onClick={() => void markSelectedRead()}>
                      Mark selected read ({selectedIds.size})
                    </button>
                  </div>
                )}
                {notifications.slice(0, 12).map(notification => (
                  <NotificationRow
                    key={notification.id}
                    notification={{
                      id: notification.id,
                      type: notification.type,
                      title: notification.title,
                      message: notification.message,
                      timestamp: notification.timestamp,
                      read: notification.read,
                      isPriority: notification.isPriority,
                      projectId: notification.projectId,
                    }}
                    clickable={isNotificationClickable(notification)}
                    onClick={() => handleNotificationClick(notification)}
                    showSelect
                    selected={selectedIds.has(notification.id)}
                    onToggleSelect={() => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(notification.id)) next.delete(notification.id);
                        else next.add(notification.id);
                        return next;
                      });
                    }}
                    onToggleRead={() => void toggleRead(notification)}
                    onMuteProject={
                      notification.projectId
                        ? () => void setProjectMode(notification.projectId, 'muted')
                        : undefined
                    }
                    onMentionsOnly={
                      notification.projectId
                        ? () => void setProjectMode(notification.projectId, 'mentions')
                        : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>

          <button
            className="notification-view-all"
            onClick={() => { closeMenu(); navigate('/notifications'); }}
          >
            View all notifications <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
